import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Users, Shield, BookOpen, TrendingUp, Target, Lightbulb } from 'lucide-react';
export function EducationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-398">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-399">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-400">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-401">
            <span className="w-8 h-px bg-teal" data-id="element-402"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-403">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-404"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-405">
            Education and Human Capability Development
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-406">
            SyNeural's platform architecture is designed to help education
            systems build stronger thinkers, more self-directed learners, and
            more capable future citizens.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-407">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-408">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-409">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding how students develop judgment, how readiness gaps
            emerge, and how schools and institutions can better connect those
            gaps to meaningful pathways for growth.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-410">
            The platform is relevant across the full educational lifecycle. At
            early stages, it can support emotional regulation, cooperation,
            self-direction, and foundational reasoning. In primary and secondary
            education, it can help strengthen logic, media literacy, planning,
            resilience against manipulation, and long-range decision-making. In
            higher education, it can support critical thinking, civic
            capability, leadership development, and readiness for professional
            and institutional responsibility.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-411">
        {/* Early Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-412">
          <div className="flex items-center gap-4 mb-6" data-id="element-413">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-414">
              <Users size={28} data-id="element-415" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-416">
              Early Development and Foundational Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-417">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-418">
              At the earliest stages, SyNeural can support the development of
              core capabilities that shape later learning and long-term
              readiness. These include emotional regulation, impulse control,
              attention stability, cooperation, frustration tolerance, and basic
              cause-and-effect reasoning. By identifying developmental
              bottlenecks early and connecting them to support pathways, the
              platform can help schools and caregivers build stronger
              foundations before problems compound over time.
            </p>
          </div>
        </motion.div>

        {/* Primary and Secondary Education */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-419">
          <div className="flex items-center gap-4 mb-6" data-id="element-420">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-421">
              <BookOpen size={28} data-id="element-422" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-423">
              Primary and Secondary Education
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-424">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-425">
              In school-age environments, SyNeural can help strengthen the
              capabilities that support both academic performance and broader
              life readiness. This includes reasoning quality, self-direction,
              media literacy, planning ability, emotional stability, and the
              ability to think through consequences. The platform can support
              schools in moving beyond narrow academic outcomes toward a fuller
              model of human capability and judgment development.
            </p>
          </div>
        </motion.div>

        {/* Media Literacy */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-426">
          <div className="flex items-center gap-4 mb-6" data-id="element-427">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-428">
              <Shield size={28} data-id="element-429" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-430">
              Media Literacy and Manipulation Resistance
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-431">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-432">
              Students are growing up in environments shaped by information
              overload, emotional framing, and highly persuasive media systems.
              SyNeural can help education systems strengthen resilience against
              propaganda, rhetorical manipulation, peer-driven narratives, and
              low-quality information environments. This makes the platform
              especially relevant for media literacy, critical reasoning, and
              the development of more independent and grounded judgment.
            </p>
          </div>
        </motion.div>

        {/* Beyond Grades */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-433">
          <div className="flex items-center gap-4 mb-6" data-id="element-434">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-435">
              <Target size={28} data-id="element-436" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-437">
              Student Development Beyond Grades
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-438">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-439">
              A major strength of the platform is that it helps make important
              human capabilities more visible beyond conventional grading
              systems. Students may have strong potential but struggle with
              execution, confidence, emotional regulation, or opportunity
              access. Others may perform academically while remaining weak in
              judgment, foresight, or resilience. SyNeural helps create a more
              complete picture of readiness by showing strengths, bottlenecks,
              and development pathways that traditional academic signals often
              miss.
            </p>
          </div>
        </motion.div>

        {/* Higher Education */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-440">
          <div className="flex items-center gap-4 mb-6" data-id="element-441">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-442">
              <GraduationCap size={28} data-id="element-443" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-444">
              Higher Education and Readiness for Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-445">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-446">
              In universities and advanced learning environments, SyNeural can
              support critical thinking, long-range planning, leadership
              development, and readiness for complex institutional and
              professional roles. It can help students better understand their
              strengths, identify practical growth areas, and prepare for paths
              that involve greater responsibility in civic life, work,
              entrepreneurship, or public service.
            </p>
          </div>
        </motion.div>

        {/* Support for Educators */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-447">
          <div className="flex items-center gap-4 mb-6" data-id="element-448">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-449">
              <Lightbulb size={28} data-id="element-450" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-451">
              Support for Educators and Institutions
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-452">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-453">
              SyNeural is designed not only for student insight, but for
              institutional improvement. Schools, universities, and educational
              leaders can use the platform to better understand where capability
              development is strong, where support systems are weak, and where
              educational models may need to evolve. This can support curriculum
              design, student support strategies, developmental planning, and
              broader efforts to connect education with long-term readiness
              rather than short-term performance alone.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-454">
        <div className="max-w-4xl mx-auto" data-id="element-455">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-456">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-457">
              Why It Matters for Education
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-458">
              Education systems do more than deliver content. They shape how
              people think, how they regulate themselves, how they handle
              complexity, and how they prepare for life inside institutions and
              society. SyNeural helps make that broader developmental mission
              more visible and more actionable. It offers a framework for
              helping students not only perform, but grow into more capable,
              resilient, and responsible individuals over time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}